/**
 * Cloud-agnostic function call adapter for Firebase callable functions.
 *
 * In **preview** environments (where `VITE_CLOUD_RUN_URL` is set), calls are
 * routed directly to the Cloud Run service via `fetch()`. This avoids deploying
 * Firebase Cloud Functions per-PR and leverages Cloud Run's concurrency / cost
 * advantages.
 *
 * In **development** and **production** (no `VITE_CLOUD_RUN_URL`), calls use
 * the standard Firebase `httpsCallable` SDK which routes to Cloud Functions.
 *
 * The callable protocol is symmetric — both paths accept `{ data }` and return
 * `{ data }` in the response.
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import { createUnexpectedError } from "../types/errors";

/** Resolved once at module load; falsy when not in a Cloud Run preview. */
const CLOUD_RUN_URL: string | undefined =
  (import.meta.env.VITE_CLOUD_RUN_URL as string | undefined) || undefined;

/**
 * Create a callable reference to a backend function.
 *
 * Returns a function with the **same shape** as Firebase `httpsCallable` —
 * `(data: TReq) => Promise<{ data: TRes }>` — so call-sites remain
 * unchanged regardless of the backend.
 */
export function getCallable<TReq = unknown, TRes = unknown>(
  functionName: string
): (data: TReq) => Promise<{ data: TRes }> {
  if (CLOUD_RUN_URL) {
    return (data: TReq) => callCloudRun<TReq, TRes>(functionName, data);
  }

  // Standard Firebase Cloud Functions path
  const callable = httpsCallable<TReq, TRes>(functions, functionName);
  return (data: TReq) => callable(data);
}

// ─── Cloud Run fetch implementation ───────────────────────────────────

interface CloudRunErrorBody {
  error?: { message?: string; status?: string };
}

async function callCloudRun<TReq, TRes>(
  functionName: string,
  data: TReq
): Promise<{ data: TRes }> {
  const url = `${CLOUD_RUN_URL}/${functionName}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data }),
    });
  } catch (error) {
    throw createUnexpectedError({
      error,
      context: `Network error calling Cloud Run function "${functionName}" at ${url}`,
    });
  }

  if (!response.ok) {
    let message = `Cloud Run call failed: HTTP ${response.status}`;
    try {
      const body = (await response.json()) as CloudRunErrorBody;
      if (body.error?.message) message = body.error.message;
    } catch {
      /* ignore parse errors — use default message */
    }
    throw new Error(message);
  }

  const json = (await response.json()) as { result: TRes };
  return { data: json.result };
}
